import { useMaintenanceCheck, MaintenanceScreen } from '../MaintenanceGate';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CreditQueryCard from './CreditQueryCard';
import { useAuth, regionsOf, regionLabel } from '../AuthContext';

const RSH_EMAILS = ['rosvinder.kaur@ninjavan.co', 'afiq.hadi@ninjavan.co'];
const HEAD_OF_SALES_EMAILS = ['dwayne.tung@ninjavan.co'];
function getUserTitle(email) {
  if (HEAD_OF_SALES_EMAILS.includes(email)) return 'Deputy Head, Ninja Mart';
  return RSH_EMAILS.includes(email) ? 'Regional Sales Head' : 'Regional Sales Supervisor';
}

const LANG = {
  en: {
    title:        'Credit Application System',
    listTitle:    (r) => `Application List (Region ${r})`,
    newRequest:   'New Request',
    signOut:      'Sign out',
    navAR:        'AR Summary',
    navCAS:       'Credit Applications',
    navMR:        'Monthly Review',
    langToggle:   'BM | EN',
    loading:      'Page is loading, please wait...',
    loadingDetail:'Details are loading, please wait...',
    error:        'Failed to load submissions.',
    noData:       'Tiada Rekod',
    refresh:      'Refresh',
    refNo:        'Ref No',
    outletId:     'Outlet ID',
    outletName:   'Outlet Name',
    applicant:    'Applicant',
    appDate:      'Application Date',
    reqLimit:     'Applied Limit',
    finalLimit:   'Approved Limit',
    status:       'Status',
    decisionDate: 'Decision Date',
    total:        'Total',
    inReview:     'In Review',
    approved:     'Approved',
    declined:     'Declined / Fail KYC',
    close:        'Close',
    filterStatus: 'All Status',
    filterApplicant: 'All Applicants',
    filterOutletId: 'Search Outlet ID...',
    filterVanId: 'Search Van ID...',
    vanId: 'Servicing Van ID',
    filterOutletName: 'Search Outlet Name...',
    filterAppDate:'Application Date',
    filterDecDate:'Decision Date',
    clearFilters: 'Clear',
    submissionDetail: 'Submission Details',
    entityType:   'Entity Type',
    regno:        'Registration No.',
    requestedLimit:'Applied Limit',
    limitJustification: 'Limit Justification',
    latePaymentResponse: 'Late Payment Response',
    otherRemarks: 'Other Remarks',
    l1kyc:        'KYC Review L1',
    l2kyc:        'KYC Review L2',
    limitDecision:'Credit Decision',
    kycStatus:    'KYC Status',
    reviewedBy:   'Reviewed By',
    approvedBy:   'Approved By',
    decidedBy:    'Decided By',
    remarks:      'Remarks',
    noRemark:     'None',
    approvalStatus: 'Approval Status',
    approvedLimit:'Approved Limit',
    decisionDateLabel: 'Decision Date',
    notYet:       'Not yet processed',
    viewDocs:     'View Documents',
    submittedOn:  'Application Date',
    submittedBy:  'Applicant',
  },
  bm: {
    title:        'Sistem Permohonan Kredit',
    listTitle:    (r) => `Senarai Permohonan (Region ${r})`,
    newRequest:   'Permohonan Baru',
    signOut:      'Log keluar',
    navAR:        'Ringkasan Kredit & Tunggakan',
    navCAS:       'Semakan Permohonan Kredit',
    navMR:        'Penilaian Kredit Bulanan',
    langToggle:   'BM | EN',
    loading:      'Halaman sedang dimuat turun, sila tunggu...',
    loadingDetail:'Butiran sedang dimuat turun, sila tunggu...',
    error:        'Gagal dimuat turun.',
    noData:       'Tiada Rekod',
    refresh:      'Muat Semula',
    refNo:        'No Rujukan',
    outletId:     'Outlet ID',
    outletName:   'Nama Outlet',
    applicant:    'Pemohon',
    appDate:      'Tarikh Permohonan',
    reqLimit:     'Jumlah Diminta',
    finalLimit:   'Jumlah Diluluskan',
    status:       'Status',
    decisionDate: 'Tarikh Keputusan',
    total:        'Jumlah',
    inReview:     'Dalam Semakan',
    approved:     'Diluluskan',
    declined:     'Ditolak / Gagal KYC',
    close:        'Tutup',
    filterStatus: 'Semua Status',
    filterApplicant: 'Semua Pemohon',
    filterOutletId: 'Cari Outlet ID...',
    filterVanId: 'Cari Van ID...',
    vanId: 'Wakil Van ID',
    filterOutletName: 'Cari Nama Outlet...',
    filterAppDate:'Tarikh Permohonan',
    filterDecDate:'Tarikh Keputusan',
    clearFilters: 'Padam',
    submissionDetail: 'Butiran Permohonan',
    entityType:   'Jenis Entiti',
    regno:        'No. Pendaftaran',
    requestedLimit:'Jumlah Diminta',
    limitJustification: 'Justifikasi Had Kredit',
    latePaymentResponse: 'Respons Pembayaran Lewat',
    otherRemarks: 'Catatan Lain',
    l1kyc:        'Semakan KYC L1',
    l2kyc:        'Semakan KYC L2',
    limitDecision:'Semakan Kredit',
    kycStatus:    'Status KYC',
    reviewedBy:   'Disemak Oleh',
    approvedBy:   'Diluluskan Oleh',
    decidedBy:    'Diputuskan Oleh',
    remarks:      'Catatan',
    noRemark:     'Tiada',
    approvalStatus: 'Status Kelulusan',
    approvedLimit:'Jumlah Diluluskan',
    decisionDateLabel: 'Tarikh Keputusan',
    notYet:       'Belum diproses',
    viewDocs:     'Lihat Dokumen',
    submittedOn:  'Tarikh Permohonan',
    submittedBy:  'Pemohon',
  },
};

const STATUS_COLOR = {
  'Maklum Balas Diperlukan':      '#c8102e',
  'Dalam Semakan KYC (L1)':      '#888',
  'Dalam Semakan KYC (L2)':      '#b06000',
  'KIV � Semakan KYC':           '#7b1fa2',
  'Dalam Semakan Kredit':        '#1967d2',
  'Eskalasi Had Kredit':         '#7b1fa2',
  'Diluluskan Sepenuhnya':       '#1e7e34',
  'Diluluskan (Had Lebih Rendah)':'#1e7e34',
  'Ditolak':                     '#c5221f',
  'Had Dibatal':                 '#c5221f',
  'Gagal KYC':                   '#c5221f',
};

const STATUS_EN = {
  'Maklum Balas Diperlukan':      'Pending Sales Reply',
  'Dalam Semakan KYC (L1)':      'KYC Review (L1)',
  'Dalam Semakan KYC (L2)':      'KYC Review (L2)',
  'KIV � Semakan KYC':           'KIV � KYC Review',
  'Dalam Semakan Kredit':        'Credit Review',
  'Eskalasi Had Kredit':         'Limit Escalation',
  'Diluluskan Sepenuhnya':       'Approved as Requested',
  'Diluluskan (Had Lebih Rendah)':'Approved with Lower Limit',
  'Ditolak':                     'Declined',
  'Had Dibatal':                 'Limit Revoked',
  'Gagal KYC':                   'Fail KYC',
};

function deriveStatus({ aiaDecision, finalLimit, reqLimit, approver, approvalDt, l1Prepared, l2Status }) {
  // Legacy rows: approver filled but no AIA
  if (approver && !aiaDecision) {
    if (finalLimit === 0)                                    return 'Ditolak';
    if (finalLimit != null && finalLimit >= reqLimit)        return 'Diluluskan Sepenuhnya';
    if (finalLimit != null)                                  return 'Diluluskan (Had Lebih Rendah)';
  }
  // AIA decision present
  if (aiaDecision) {
    if (aiaDecision.startsWith('Approved')) {
      return (finalLimit != null && reqLimit != null && finalLimit >= reqLimit)
        ? 'Diluluskan Sepenuhnya' : 'Diluluskan (Had Lebih Rendah)';
    }
    if (aiaDecision === 'Declined')                          return 'Ditolak';
    if (aiaDecision === 'Limit Revoked')                     return 'Had Dibatal';
    if (aiaDecision === 'Fail KYC')                          return 'Gagal KYC';
    if (aiaDecision === 'Pending L2 Limit Approval')         return 'Eskalasi Had Kredit';
  }
  if (aiaDecision === 'Waiting for Sales Reply')             return 'Maklum Balas Diperlukan';
  if (l2Status === 'Reject')                                 return 'Gagal KYC';
  if (l2Status === 'KIV')                                    return 'KIV � Semakan KYC';
  if (l2Status === 'Approve' && !approvalDt)                 return 'Dalam Semakan Kredit';
  if (l2Status === 'Approve' && approvalDt)                  return 'Dalam Semakan Kredit';
  if (l1Prepared)                                            return 'Dalam Semakan KYC (L2)';
  return 'Dalam Semakan KYC (L1)';
}

function fmt(n) { return n != null ? 'RM ' + Number(n).toLocaleString() : '-'; }
function fmtDate(val) {
  if (!val) return '-';
  const d = new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const s = {
  page:     { minHeight: '100vh', background: '#f0f2f5', fontFamily: 'system-ui,-apple-system,sans-serif' },
  nav:      { background: '#c8102e', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 },
  navL:     { display: 'flex', alignItems: 'center', gap: 12 },
  navT:     { color: '#fff', fontWeight: 700, fontSize: 15 },
  navR:     { display: 'flex', alignItems: 'center', gap: 12 },
  navInfo:  { textAlign: 'right' },
  navName:  { color: '#fff', fontWeight: 600, fontSize: 13 },
  navSub:   { color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 2 },
  langToggle: { background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  logoutB:  { background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  body:     { maxWidth: 1200, margin: '0 auto', padding: '20px 16px' },
  header:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  h1:       { fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  actions:  { display: 'flex', gap: 8 },
  newBtn:   { background: '#c8102e', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  refBtn:   { background: '#fff', color: '#333', border: '1px solid #ddd', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13 },
  stats:    { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 },
  stat:     { background: '#fff', borderRadius: 10, padding: '14px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  statN:    { fontSize: 26, fontWeight: 700 },
  statL:    { fontSize: 11, color: '#888', marginTop: 2 },
  filters:  { background: '#fff', borderRadius: 10, padding: '12px 16px', marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' },
  fGrp:     { display: 'flex', flexDirection: 'column', gap: 3 },
  fLbl:     { fontSize: 11, color: '#888', fontWeight: 600 },
  fSel:     { padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, background: '#fff' },
  fInp:     { padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, width: 140 },
  clearBtn: { padding: '6px 14px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#555', alignSelf: 'flex-end' },
  card:     { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  table:    { width: '100%', borderCollapse: 'collapse' },
  th:       { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e0e0e0', background: '#f0f2f5', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 10 },
  td:       { padding: '11px 12px', fontSize: 13, borderBottom: '1px solid #f5f5f5', verticalAlign: 'middle' },
  trow:     { cursor: 'pointer', transition: 'background 0.1s' },
  badge:    { display: 'inline-block', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  empty:    { padding: 48, textAlign: 'center', color: '#888' },
  loadBox:  { padding: 48, textAlign: 'center', color: '#555' },
  spinner:  { display: 'inline-block', width: 24, height: 24, border: '3px solid #eee', borderTopColor: '#c8102e', borderRadius: '50%', animation: 'spin 0.7s linear infinite', marginBottom: 12 },
  spinnerSm:{ display: 'inline-block', width: 16, height: 16, border: '2px solid #eee', borderTopColor: '#c8102e', borderRadius: '50%', animation: 'spin 0.7s linear infinite', marginBottom: 8 },
  // Modal
  overlay:  { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  modal:    { background: '#f0f2f5', width: '100%', maxWidth: 640, maxHeight: '92vh', borderRadius: '16px 16px 0 0', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  modalH:   { background: '#fff', padding: '16px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 },
  modalT:   { fontSize: 15, fontWeight: 700, color: '#1a1a2e' },
  closeBtn: { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' },
  modalB:   { overflowY: 'auto', padding: '12px 16px', flex: 1 },
  mCard:    { background: '#fff', borderRadius: 10, padding: '14px 16px', marginBottom: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  mCardHL:  { background: '#fff', borderRadius: 10, padding: '14px 16px', marginBottom: 10, boxShadow: '0 2px 8px rgba(200,16,46,0.15)', border: '1px solid rgba(200,16,46,0.2)' },
  mTitle:   { fontSize: 12, fontWeight: 700, color: '#888', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid #f0f0f0', textTransform: 'uppercase', letterSpacing: '0.5px' },
  mTitleHL: { fontSize: 12, fontWeight: 700, color: '#c8102e', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid rgba(200,16,46,0.2)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  kv2:      { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' },
  kvLabel:  { fontSize: 11, color: '#888', marginBottom: 2 },
  kvValue:  { fontSize: 13, fontWeight: 500, color: '#1a1a2e', lineHeight: 1.4 },
  kvGrey:   { fontSize: 13, color: '#bbb', fontStyle: 'italic' },
};

function KV({ label, value, grey, full }) {
  return (
    <div style={full ? { gridColumn: '1/-1' } : {}}>
      <div style={s.kvLabel}>{label}</div>
      {grey
        ? <div style={s.kvGrey}>{value}</div>
        : <div style={s.kvValue}>{value || '-'}</div>
      }
    </div>
  );
}

function StatusBadge({ status, color }) {
  return (
    <span style={{ ...s.badge, background: color + '22', color }}>
      {status}
    </span>
  );
}

function DetailModal({ mcRow, aiaSheetRow, onClose, t, lang, user }) {
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!mcRow) return;
    setLoading(true); setDetail(null);
    const url = aiaSheetRow && aiaSheetRow !== -1
      ? `/api/cas/submission/${mcRow}?aiaRow=${aiaSheetRow}`
      : `/api/cas/submission/${mcRow}`;
    fetch(url)
      .then(r => r.json())
      .then(d => { setDetail(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [mcRow]);

  function limitStatusLabel() {
    if (!detail) return '-';
    const dec = detail.aiaDecision?.decision || '';
    const fl  = detail.aiaDecision?.finalLimit ?? detail.limitDecisionMC?.finalLimit;
    const rl  = detail.reqLimit;
    // Legacy rows without AIA � infer from MC cols
    const hasDecision = dec || detail.limitDecisionMC?.approver;
    if (!hasDecision) return '-';
    const isApproved = dec.startsWith('Approved') || (!dec && detail.limitDecisionMC?.approver);
    if (!isApproved) return lang === 'en' ? (dec === 'Declined' ? 'Declined' : 'Fail KYC') : (dec === 'Declined' ? 'Ditolak' : 'Gagal KYC');
    // finalLimit === 0 means decline regardless of approval flag
    if (fl === 0 || fl === null) return lang === 'en' ? 'Declined' : 'Ditolak';
    const bmLabel = (fl != null && rl != null && fl >= rl)
      ? 'Diluluskan Sepenuhnya'
      : 'Diluluskan (Had Lebih Rendah)';
    return lang === 'en' ? STATUS_EN[bmLabel] : bmLabel;
  }

  function limitStatusColor() {
    const label = limitStatusLabel();
    if (label.includes('Dilulus') || label.includes('Approved')) return '#1e7e34';
    return '#c5221f';
  }

  const l1PrepParts = (detail?.l1?.prepared || '').split(' - ');
  const l1PrepBy    = l1PrepParts[0] || '';
  const l1PrepDate  = l1PrepParts[1] || '';
  const remarks     = detail?.remarks || {};

  // Show limit card if either L2 approved OR legacy row with limit decision
  const hasLimitDecision = detail?.aiaDecision?.decision || detail?.limitDecisionMC?.approver;
  const showLimitCard    = detail?.l2?.status === 'Approve' || (detail?.limitDecisionMC?.approver && !detail?.l2?.status);

  const finalLimitDisplay = detail?.aiaDecision?.finalLimit ?? detail?.limitDecisionMC?.finalLimit;
  const approverDisplay   = detail?.aiaDecision?.approver   || detail?.limitDecisionMC?.approver || '';
  const dateDisplay       = detail?.aiaDecision?.date       || detail?.limitDecisionMC?.date     || '';
  const remarksDisplay    = detail?.aiaDecision?.remarks    || detail?.limitDecisionMC?.remarks  || '';

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.modalH}>
          <div style={s.modalT}>{t.submissionDetail} - #{mcRow}</div>
          <button style={s.closeBtn} onClick={onClose}>x</button>
        </div>
        <div style={s.modalB}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
              <div style={s.spinnerSm} />
              <div style={{ fontSize: 13 }}>{t.loadingDetail}</div>
            </div>
          ) : !detail ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#c5221f', fontSize: 13 }}>Failed to load.</div>
          ) : (
            <>
              {/* Submission */}
              <div style={s.mCard}>
                <div style={s.mTitle}>{t.submissionDetail}</div>
                <div style={s.kv2}>
                  <KV label={t.outletId}        value={detail.outletId} />
                  <KV label={t.submittedOn}      value={detail.submittedAt} />
                  <KV label={t.entityType}       value={detail.entityType} />
                  <KV label={t.regno}            value={detail.regno} />
                  <KV label={t.requestedLimit}   value={fmt(detail.reqLimit)} />
                  <KV label={t.submittedBy}      value={detail.submittedBy} />
                </div>
                {(remarks.limit_justification || remarks.late_payment_response || remarks.other_remarks) && (
                  <div style={{ marginTop: 10, borderTop: '1px solid #f5f5f5', paddingTop: 10 }}>
                    {remarks.limit_justification && <KV full label={t.limitJustification} value={remarks.limit_justification} />}
                    {remarks.late_payment_response && <div style={{ marginTop: 8 }}><KV full label={t.latePaymentResponse} value={remarks.late_payment_response} /></div>}
                    {remarks.other_remarks && <div style={{ marginTop: 8 }}><KV full label={t.otherRemarks} value={remarks.other_remarks} /></div>}
                  </div>
                )}
                {detail.driveUrl && (
                  <div style={{ marginTop: 10 }}>
                    <a href={detail.driveUrl.split(',')[0].trim()} target="_blank" rel="noreferrer"
                      style={{ fontSize: 12, color: '#1967d2' }}>{t.viewDocs}</a>
                  </div>
                )}
              </div>

              {/* L1 KYC */}
              <div style={s.mCard}>
                <div style={s.mTitle}>{t.l1kyc}</div>
                {!detail.l1?.status ? (
                  <div style={s.kvGrey}>{t.notYet}</div>
                ) : (
                  <div style={s.kv2}>
                    <KV label={t.kycStatus} value={
                      <StatusBadge status={detail.l1.status}
                        color={detail.l1.status === 'Pass' ? '#1e7e34' : detail.l1.status === 'Escalate' ? '#b06000' : '#c5221f'} />
                    } />
                    <KV label={t.reviewedBy} value={`${l1PrepBy}${l1PrepDate ? ' - ' + l1PrepDate : ''}`} />
                    <div style={{ gridColumn: '1/-1' }}>
                      <div style={s.kvLabel}>{t.remarks}</div>
                      {detail.l1.remarks
                        ? <div style={s.kvValue}>{detail.l1.remarks}</div>
                        : <div style={s.kvGrey}>{t.noRemark}</div>
                      }
                    </div>
                  </div>
                )}
              </div>

              {/* L2 KYC */}
              <div style={s.mCard}>
                <div style={s.mTitle}>{t.l2kyc}</div>
                {!detail.l2?.status ? (
                  <div style={s.kvGrey}>{t.notYet}</div>
                ) : (
                  <div style={s.kv2}>
                    <KV label={t.kycStatus} value={
                      <StatusBadge status={detail.l2.status}
                        color={detail.l2.status === 'Approve' ? '#1e7e34' : detail.l2.status === 'KIV' ? '#7b1fa2' : '#c5221f'} />
                    } />
                    <KV label={t.approvedBy} value={`${detail.l2.approver || ''}${detail.l2.date ? ' - ' + detail.l2.date : ''}`} />
                    {detail.l2.rejectionReason && (
                      <div style={{ gridColumn: '1/-1' }}>
                        <KV label={lang === 'en' ? 'Rejection Reason' : 'Sebab Penolakan'} value={detail.l2.rejectionReason} />
                      </div>
                    )}
                    <div style={{ gridColumn: '1/-1' }}>
                      <div style={s.kvLabel}>{t.remarks}</div>
                      {detail.l2.remarks
                        ? <div style={s.kvValue}>{detail.l2.remarks}</div>
                        : <div style={s.kvGrey}>{t.noRemark}</div>
                      }
                    </div>
                  </div>
                )}
              </div>

              {/* Credit Decision */}
              <div style={showLimitCard && hasLimitDecision ? s.mCardHL : s.mCard}>
                <div style={showLimitCard && hasLimitDecision ? s.mTitleHL : s.mTitle}>{t.limitDecision}</div>
                {!showLimitCard ? (
                  <div style={s.kvGrey}>{t.notYet}</div>
                ) : !hasLimitDecision ? (
                  <div style={s.kvGrey}>{t.notYet}</div>
                ) : (
                  <div style={s.kv2}>
                    <KV label={t.approvalStatus} value={
                      <StatusBadge status={limitStatusLabel()} color={limitStatusColor()} />
                    } />
                    <KV label={t.approvedLimit} value={detail?.aiaDecision?.decision === 'Declined' ? 'N/A' : fmt(finalLimitDisplay)} />
                    <KV label={t.decidedBy}           value={`${approverDisplay}${dateDisplay ? ' - ' + dateDisplay : ''}`} />
                    <div style={{ gridColumn: '1/-1' }}>
                      <div style={s.kvLabel}>{t.remarks}</div>
                      {remarksDisplay
                        ? <div style={s.kvValue}>{remarksDisplay}</div>
                        : <div style={s.kvGrey}>{t.noRemark}</div>
                      }
                    </div>
                  </div>
                )}
              </div>
            {/* Credit Query � show when query exists or status indicates it */}
            {(detail.aiaDecision?.decision === 'Waiting for Sales Reply' ||
              detail.aiaDecision?.decision === 'Credit Query') && (
              <CreditQueryCard
                mcRow={mcRow}
                outletId={detail.outletId}
                outletName={detail.outletName}
                role="sales"
                senderName={user?.displayName || ''}
                senderEmail={user?.email || ''}
                recipientEmail={detail.approverEmail || ''}
                lang={lang}
                onQuerySent={() => {}}
              />
            )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function CASDBSidebar({ open, onClose, navigate, t }) {
  if (!open) return null;
  const items = [
    { label: t.navAR,  path: '/cas/ar',       active: false },
    { label: t.navCAS, path: '/cas/dashboard', active: true  },
    { label: t.navMR,  path: '/cas/monthly-review', active: false },
  ];
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300 }} onClick={onClose} />
      <div style={{ position: 'fixed', left: 0, top: 0, bottom: 0, width: 240, background: '#fff', zIndex: 400, boxShadow: '4px 0 16px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ background: '#c8102e', padding: '20px 16px 16px', color: '#fff' }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Ninja Mart Credit</div>
          <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>Sales Portal</div>
        </div>
        <div style={{ flex: 1, padding: '12px 0' }}>
          {items.map(item => (
            <div key={item.path}
              style={{ padding: '12px 20px', cursor: 'pointer', fontSize: 13, fontWeight: item.active ? 700 : 400, color: item.active ? '#c8102e' : '#555', background: item.active ? '#fff0f0' : 'transparent', borderLeft: item.active ? '3px solid #c8102e' : '3px solid transparent' }}
              onClick={() => { navigate(item.path); onClose(); }}>
              {item.label}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default function CASDashboard() {
  const { user, logout } = useAuth();
  const maint = useMaintenanceCheck('sales');
  const navigate  = useNavigate();
  const [lang, setLang]     = useState(() => localStorage.getItem('cas_lang') || 'en');
  const [data, setData]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [openRow, setOpenRow]       = useState(null); // { mcRow, aiaSheetRow }
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const [sidebarOpen, setSidebarOpen]   = useState(false);

  const [filterStatus,      setFilterStatus]      = useState('');
  const [filterApplicant,   setFilterApplicant]   = useState('');
  const [filterOutletId,    setFilterOutletId]    = useState('');
  const [filterVanId,       setFilterVanId]       = useState('');
  const [onLeaveToday, setOnLeaveToday] = useState([]);

  useEffect(() => {
    fetch('/api/leave-roster/today').then(r => r.json()).then(setOnLeaveToday).catch(() => {});
  }, []);
  const [filterOutletName,  setFilterOutletName]  = useState('');
  const [filterAppDate,     setFilterAppDate]     = useState('');
  const [filterDecDate,     setFilterDecDate]     = useState('');
  const [regionFilter,      setRegionFilter]      = useState('');

  const t         = LANG[lang];
  const userTitle = getUserTitle(user.email);

  function toggleLang() {
    const next = lang === 'en' ? 'bm' : 'en';
    setLang(next); localStorage.setItem('cas_lang', next);
  }

  async function load() {
    setLoading(true); setError('');
    try {
      const res = await fetch(`/api/cas/submissions?region=${encodeURIComponent(regionsOf(user).join(','))}&email=${encodeURIComponent(user.email)}`);
      if (!res.ok) throw new Error(await res.text());
      const raw = await res.json();
      const enriched = raw.map(r => ({
        ...r,
        statusBM: deriveStatus({
          aiaDecision: r.aiaDecision,
          finalLimit:  r.finalLimit,
          reqLimit:    r.reqLimit,
          approver:    r.approver,
          approvalDt:  r.approvalDate,
          l1Prepared:  r.l1Prepared,
          l2Status:    r.l2Status,
        }),
      }));
      // Pin Pending Sales Reply to top
    enriched.sort((a, b) => {
      const ap = a.statusBM === 'Maklum Balas Diperlukan' ? 0 : 1;
      const bp = b.statusBM === 'Maklum Balas Diperlukan' ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return (b.submittedAt || '').localeCompare(a.submittedAt || '');
    });
    setData(enriched);
    } catch (e) { setError(t.error); }
    setLoading(false);
  }

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!profileOpen) return;
    // Only close on a mousedown OUTSIDE the dropdown � previously this fired
    // unconditionally on any mousedown anywhere, which closed (and unmounted)
    // the dropdown before a click on Sign Out inside it could ever register,
    // since mousedown fires before click.
    const close = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [profileOpen]);

  const applicants = useMemo(() => [...new Set(data.map(r => r.submittedBy).filter(Boolean))], [data]);
  const statuses   = useMemo(() => [...new Set(data.map(r => r.statusBM).filter(Boolean))], [data]);
  const regions    = useMemo(() => [...new Set(data.map(r => r.region).filter(Boolean))], [data]);

  const filtered = useMemo(() => data.filter(r => {
    if (filterStatus     && r.statusBM !== filterStatus)                                          return false;
    if (filterApplicant  && r.submittedBy !== filterApplicant)                                    return false;
    if (filterOutletId   && !r.outletId.includes(filterOutletId.toUpperCase()))                   return false;
    if (filterVanId      && !(r.vanId || '').toUpperCase().includes(filterVanId.toUpperCase()))      return false;
    if (filterOutletName && !(r.outletName || '').toUpperCase().includes(filterOutletName.toUpperCase())) return false;
    if (regionFilter && r.region !== regionFilter) return false;
    // Application date � compare ISO date string slice vs YYYY-MM-DD input
    if (filterAppDate) {
      if (!r.submittedAt) return false;
      const localDate = new Date(new Date(r.submittedAt).getTime() + 8 * 60 * 60 * 1000);
      if (localDate.toISOString().slice(0, 10) !== filterAppDate) return false;
    }
    // Approval date � only show rows that have an approval date matching the filter
    // Undecided rows (no approvalDate) are excluded when date filter is active
    if (filterDecDate) {
      if (!r.approvalDate) return false;
      // approvalDate from sheet may be "30/06/2026", "30 Jun 2026" etc � normalise to YYYY-MM-DD
      const normaliseDate = (s) => {
        if (!s) return '';
        // DD/MM/YYYY or DD-MM-YYYY
        const m1 = s.match(/(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})/);
        if (m1) return `${m1[3]}-${m1[2].padStart(2,'0')}-${m1[1].padStart(2,'0')}`;
        // "30 Jun 2026" or "30 June 2026"
        const m2 = s.match(/(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/);
        if (m2) {
          const months = {jan:'01',feb:'02',mar:'03',apr:'04',may:'05',jun:'06',jul:'07',aug:'08',sep:'09',oct:'10',nov:'11',dec:'12'};
          const mo = months[m2[2].slice(0,3).toLowerCase()];
          if (mo) return `${m2[3]}-${mo}-${m2[1].padStart(2,'0')}`;
        }
        // ISO or other parseable
        const d = new Date(s);
        if (!isNaN(d)) return d.toISOString().slice(0, 10);
        return s;
      };
      if (normaliseDate(r.approvalDate) !== filterDecDate) return false;
    }
    return true;
  }), [data, filterStatus, filterApplicant, filterOutletId, filterOutletName, filterVanId, filterAppDate, filterDecDate]);

  function clearFilters() {
    setFilterStatus(''); setFilterApplicant(''); setFilterOutletId('');
    setFilterOutletName(''); setFilterAppDate(''); setFilterDecDate(''); setRegionFilter('');
  }

  const total    = filtered.length;
  const inReview = filtered.filter(r => !r.statusBM.startsWith('Dilulus') && r.statusBM !== 'Ditolak' && r.statusBM !== 'Gagal KYC').length;
  const approved = filtered.filter(r => r.statusBM.startsWith('Dilulus')).length;
  const declined = filtered.filter(r => r.statusBM === 'Ditolak' || r.statusBM === 'Had Dibatal' || r.statusBM === 'Gagal KYC').length;

  if (maint.blocked) return <MaintenanceScreen />;
  return (
    <div style={s.page}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @media(max-width:600px){
          .cas-db-stats{grid-template-columns:repeat(2,1fr)!important;}
          .cas-db-logo{display:none!important;}
          .cas-db-nav-title{display:none!important;}
          .cas-db-nav-sub{display:none!important;}
          .cas-db-fgrp{flex:1 1 calc(50% - 5px)!important;min-width:120px!important;}
          .cas-db-fgrp input,.cas-db-fgrp select{width:100%!important;box-sizing:border-box!important;}
          .cas-db-fgrp-date{flex:1 1 100%!important;}
          .cas-db-h1{font-size:14px!important;}
        }
      `}</style>

      <CASDBSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} navigate={navigate} t={t} />
      <nav style={s.nav}>
        <div style={s.navL}>
          <button style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 6, padding: '6px 8px', cursor: 'pointer', color: '#fff', fontSize: 16, lineHeight: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3 }}
            onClick={() => setSidebarOpen(true)}>
            <span style={{ display:'block', width:18, height:2, background:'#fff', borderRadius:1 }} />
            <span style={{ display:'block', width:18, height:2, background:'#fff', borderRadius:1 }} />
            <span style={{ display:'block', width:18, height:2, background:'#fff', borderRadius:1 }} />
          </button>
          <img
            src="/logo-wordmark.svg"
            alt="Ninja Mart"
            width={28 * 3.84}
            height={28}
            style={{ height: 28, width: 28 * 3.84, objectFit: 'contain' }}
            className="cas-db-logo"
            onError={e => { e.target.style.display = 'none'; }} />
          <div style={s.navT} className="cas-db-nav-title">{t.title}</div>
        </div>
        <div style={s.navR}>
          <div style={{ position: 'relative' }} ref={profileRef}>
            <div style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
              onClick={e => { e.stopPropagation(); setProfileOpen(p => !p); }}>
              <div style={{ fontWeight: 600, color: '#fff', fontSize: 13 }}>{user.displayName}<span style={{ display:'inline-block', width:0, height:0, borderLeft:'4px solid transparent', borderRight:'4px solid transparent', borderTop:'5px solid #fff', marginLeft:5, verticalAlign:'middle' }} /></div>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 1 }} className="cas-db-nav-sub">{userTitle} - {regionLabel(user)}</div>
            </div>
            {profileOpen && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, background: '#fff', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', minWidth: 160, zIndex: 200, overflow: 'hidden' }}>
                <button style={{ width: '100%', padding: '12px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 13, color: '#c5221f', fontWeight: 600 }}
                  onClick={e => { e.stopPropagation(); logout(); }}>{t.signOut}</button>
              </div>
            )}
          </div>
          <button style={s.langToggle} onClick={toggleLang}>
            <span style={{ opacity: lang === 'bm' ? 1 : 0.5 }}>BM</span>
            {' | '}
            <span style={{ opacity: lang === 'en' ? 1 : 0.5 }}>EN</span>
          </button>
        </div>
      </nav>

      <div style={s.body}>
        {onLeaveToday.length > 0 && (
          <div style={{ background: '#fff8e1', border: '1px solid #f0d060', borderRadius: 8, padding: '8px 14px', marginBottom: 12, fontSize: 12, color: '#7a5c00' }}>
            {onLeaveToday.map((l, i) => (
              <div key={i}>
                {l.user_name} is on leave until {new Date(l.end_date).toLocaleDateString('en-GB')} - {l.coverage_note || `${l.relief_name} covering`}
              </div>
            ))}
          </div>
        )}
        <div style={s.header}>
                    <div style={s.h1} className="cas-db-h1">{t.listTitle(regionLabel(user))}</div>
          <div style={s.actions}>
            <button style={s.refBtn} onClick={() => { clearFilters(); load(); }}>{t.refresh}</button>
            <button style={s.newBtn} onClick={() => navigate('/cas')}>{t.newRequest}</button>
          </div>
        </div>

        <div style={s.stats} className="cas-db-stats">
          {[
            { n: total,    l: t.total,    c: '#1a1a2e' },
            { n: inReview, l: t.inReview, c: '#b06000' },
            { n: approved, l: t.approved, c: '#1e7e34' },
            { n: declined, l: t.declined, c: '#c5221f' },
          ].map((s2, i) => (
            <div key={i} style={s.stat}>
              <div style={{ ...s.statN, color: s2.c }}>{s2.n}</div>
              <div style={s.statL}>{s2.l}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={s.filters}>
          <div style={s.fGrp} className="cas-db-fgrp">
            <span style={s.fLbl}>{t.outletId}</span>
            <input style={s.fInp} placeholder={t.filterOutletId}
              value={filterOutletId} onChange={e => setFilterOutletId(e.target.value)} />
          </div>
          <div style={s.fGrp} className="cas-db-fgrp">
            <span style={s.fLbl}>{t.vanId}</span>
            <input style={s.fInp} placeholder={t.filterVanId}
              value={filterVanId} onChange={e => setFilterVanId(e.target.value)} />
          </div>
          <div style={s.fGrp} className="cas-db-fgrp">
            <span style={s.fLbl}>{t.outletName}</span>
            <input style={s.fInp} placeholder={t.filterOutletName}
              value={filterOutletName} onChange={e => setFilterOutletName(e.target.value)} />
          </div>
          <div style={s.fGrp} className="cas-db-fgrp">
            <span style={s.fLbl}>{t.status}</span>
            <select style={s.fSel} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">{t.filterStatus}</option>
              {statuses.map(st => (
                <option key={st} value={st}>{lang === 'en' ? (STATUS_EN[st] || st) : st}</option>
              ))}
            </select>
          </div>
          <div style={s.fGrp} className="cas-db-fgrp">
            <span style={s.fLbl}>{t.applicant}</span>
            <select style={s.fSel} value={filterApplicant} onChange={e => setFilterApplicant(e.target.value)}>
              <option value="">{t.filterApplicant}</option>
              {applicants.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          {regions.length > 1 && (
            <div style={s.fGrp} className="cas-db-fgrp">
              <span style={s.fLbl}>{lang === 'en' ? 'Region' : 'Region'}</span>
              <select style={s.fSel} value={regionFilter} onChange={e => setRegionFilter(e.target.value)}>
                <option value="">{lang === 'en' ? 'All Regions' : 'Semua Region'}</option>
                {regions.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          )}
          <div style={s.fGrp} className="cas-db-fgrp cas-db-fgrp-date">
            <span style={s.fLbl}>{t.filterAppDate}</span>
            <input type="date" style={s.fInp} value={filterAppDate} onChange={e => setFilterAppDate(e.target.value)} />
          </div>
          <div style={s.fGrp} className="cas-db-fgrp cas-db-fgrp-date">
            <span style={s.fLbl}>{t.filterDecDate}</span>
            <input type="date" style={s.fInp} value={filterDecDate} onChange={e => setFilterDecDate(e.target.value)} />
          </div>
          <button style={s.clearBtn} onClick={clearFilters}>{t.clearFilters}</button>
        </div>

        {/* Table */}
        <div style={s.card}>
          {loading ? (
            <div style={s.loadBox}>
              <div style={s.spinner} />
              <div style={{ fontSize: 13 }}>{t.loading}</div>
            </div>
          ) : error ? (
            <div style={{ ...s.empty, color: '#c5221f' }}>{error}</div>
          ) : filtered.length === 0 ? (
            <div style={s.empty}>{t.noData}</div>
          ) : (
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 320px)' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>{t.refNo}</th>
                    <th style={s.th}>{t.outletId}</th>
                    <th style={s.th}>{t.outletName}</th>
                    <th style={s.th}>{t.vanId}</th>
                    <th style={s.th}>{t.applicant}</th>
                    <th style={s.th}>{t.appDate}</th>
                    <th style={s.th}>{t.reqLimit}</th>
                    <th style={s.th}>{t.finalLimit}</th>
                    <th style={{ ...s.th, minWidth: 180, whiteSpace: 'nowrap' }}>{t.status}</th>
                    <th style={s.th}>{t.decisionDate}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(row => {
                    const bm    = row.statusBM || 'Dalam Semakan KYC (L1)';
                    const color = STATUS_COLOR[bm] || '#888';
                    const label = lang === 'en' ? (STATUS_EN[bm] || bm) : bm;
                    return (
                      <tr key={row.mcRow}
                        style={{ ...s.trow, borderLeft: `3px solid ${color}` }}
                        onClick={() => setOpenRow({ mcRow: row.mcRow, aiaSheetRow: row.aiaSheetRow })}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <td style={s.td}><strong>#{row.mcRow}</strong></td>
                        <td style={s.td}>{row.outletId}</td>
                        <td style={s.td}>{row.outletName || '-'}</td>
                        <td style={s.td}>{row.vanId}</td>
                        <td style={s.td}>{row.submittedBy}</td>
                        <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{fmtDate(row.submittedAt)}</td>
                        <td style={s.td}>{fmt(row.reqLimit)}</td>
                        <td style={s.td}>{(() => {
                          const bm = row.statusBM || '';
                          if (bm.startsWith('Dilulus') && row.finalLimit > 0) return fmt(row.finalLimit);
                          if (bm === 'Ditolak' || bm === 'Had Dibatal' || bm === 'Gagal KYC') return <span style={{ color: '#bbb' }}>-</span>;
                          return <span style={{ color: '#b06000', fontSize: 12 }}>{lang === 'en' ? 'In Review' : 'Dalam Proses'}</span>;
                        })()}</td>
                        <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                          <span style={{ ...s.badge, background: color + '22', color }}>{label}</span>
                        </td>
                        <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{(() => {
                          const bm = row.statusBM || '';
                          if (bm === 'Gagal KYC') return <span style={{ color: '#bbb' }}>-</span>;
                          if (bm.startsWith('Dilulus') || bm === 'Ditolak' || bm === 'Had Dibatal') return row.approvalDate || '-';
                          return <span style={{ color: '#b06000', fontSize: 12 }}>{lang === 'en' ? 'In Review' : 'Dalam Proses'}</span>;
                        })()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {openRow && (
        <DetailModal
          mcRow={openRow.mcRow}
          aiaSheetRow={openRow.aiaSheetRow}
          onClose={() => setOpenRow(null)}
          t={t} lang={lang} user={user}
        />
      )}
    </div>
  );
}